<template>
  <NovaSortableList
    v-slot="{ item: highlight }"
    :model-value="sortableItems"
    layout="list"
    :disabled="sortableItems.length <= 1 || disabled"
    :options="{
      disableDragHandler: true,
    }"
    :locked="locked"
    class="EditableList mt-2"
    data-testid="highlights-wrapper-custom-list"
    @update:model-value="(items) => handleListReorder(items)"
  >
    <ListRow
      data-testid="highlights-wrapper-list-row"
      class="ListItem"
      :added-items="addedItems"
      :removed-items="removedItems"
      :modified-items="modifiedItems"
      :editable="editable"
      :disabled="disabled"
      :highlight="highlight"
      :index="highlight.visualization_order"
      :locked="locked"
      :theme="theme"
      @row:update="(value) => list.edit(highlight.id, value)"
      @row:delete="list.del(highlight.id)"
      @row:is-editing="(value) => (locked = value)"
    />
  </NovaSortableList>
</template>

<script setup lang="ts">
import { GenericHighlight } from "@/types/Highlights";
import type { ModifiedItem } from "@/features/experience-highlights/utils/highlights-diff-utils";
import NovaSortableList from "@/ui-kit/NovaSortableList/NovaSortableList.vue";
import ListRow from "./ListRow.vue";

export interface Props {
  modelValue: GenericHighlight[];
  disabled?: boolean;
  editable?: boolean;
  theme: "primary" | "secondary" | "disabled";
  removedItems?: GenericHighlight[];
  addedItems?: GenericHighlight[];
  modifiedItems?: ModifiedItem[];
}

interface Events {
  (e: "update:modelValue", value: GenericHighlight[]): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Events>();

const locked = ref(false);
const list = useManageableList();

const sortableItems = computed<GenericHighlight[]>(() => {
  const visibleItems = props.modelValue
    .filter((item) => item.action !== "REMOVE" && item.action !== "DELETE")
    .sort((a, b) => (a.visualization_order || 0) - (b.visualization_order || 0));

  return visibleItems.map((item, index) => ({
    ...item,
    visualization_order: index + 1,
  }));
});

// Remove the watcher since edit and del functions now emit directly

// We do not use the index to manage the array operation but instead a local ID.
// The index contains elements that must be tracked, but not displayed.
// The  displayableCustomItems index will not match the model.$list index.
function useManageableList() {
  const items = computed(() => props.modelValue);

  return {
    edit,
    del,
    items,
  };

  function edit(id: GenericHighlight["id"], name: string) {
    const updatedItems = [...items.value];
    const index = updatedItems.findIndex((i) => i.id === id);

    if (index !== -1) {
      updatedItems[index] = {
        ...updatedItems[index],
        name,
        action: updatedItems[index].action === "CREATE" ? "CREATE" : "EDIT",
      };
      emits("update:modelValue", updatedItems);
    }
  }

  function del(id: GenericHighlight["id"]) {
    const updatedItems = [...items.value];
    const index = updatedItems.findIndex((i) => i.id === id);

    if (index !== -1) {
      // if the item has not yet been created,
      // we can just remove it from the array
      if (updatedItems[index].action === "CREATE") {
        updatedItems.splice(index, 1);
      } else if (updatedItems[index].id) {
        updatedItems[index] = {
          ...updatedItems[index],
          action: "DELETE",
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          action: "REMOVE",
        };
      }
      emits("update:modelValue", updatedItems);
    }
  }
}

function handleListReorder(reorderedItems: GenericHighlight[]) {
  const updatedModel = props.modelValue.map((originalItem) => {
    if (originalItem.action === "REMOVE" || originalItem.action === "DELETE") {
      return originalItem;
    }

    const reorderedIndex = reorderedItems.findIndex((item) => item.id === originalItem.id);
    if (reorderedIndex !== -1) {
      return {
        ...originalItem,
        visualization_order: reorderedIndex + 1,
        action: (originalItem.action === "CREATE" ? "CREATE" : "EDIT") as GenericHighlight["action"],
      };
    }

    return originalItem;
  });

  emits("update:modelValue", updatedModel);
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utilities";

.EditableList {
  :deep(.item) {
    border-bottom: 1px solid var(--border-default-color);
  }

  // the siblings selector (+) does not work with :deep
  :deep(.item):last-child {
    border-bottom: none;
  }
}

.ListItem {
  width: 100%;
}
</style>
